import {
  type DirectusClient,
  directusClient,
} from '@src/api/DirectusClient.ts';
import { Language } from '@funnellab/shared/src/types/directus-enums.ts';
import { UserRole } from '@funnellab/shared/src/dtos/UserDTO';
import type { CustomDirectusTypes } from '@funnellab/shared/src/types/directus.ts';
import type { DirectusUser } from '@directus/sdk';
import type { UserDto } from '@src/api/api.types.ts';
import type { RegistrationDataDTO } from '@funnellab/shared/src/dtos/auth/RegistrationDataDTO';

export type User = Omit<UserDto, 'language' | 'role'> & {
  language: Language;
  role: UserRole;
  isVerified: boolean;
};

export class AuthenticationService {
  private mapUserDtoToUser(userDto: UserDto): User {
    const role = (userDto.role?.name ?? UserRole.PUBLIC) as UserRole;
    return {
      ...userDto,
      language: (userDto.language ?? Language.DE) as Language,
      role,
      isVerified: role !== UserRole.PUBLIC,
    };
  }

  async login(username: string, password: string): Promise<boolean> {
    const authData = await directusClient.login(username, password);
    return !!authData.access_token;
  }

  logout(): Promise<void> {
    return directusClient.logout();
  }

  async getCurrentUser(): Promise<User> {
    const user = await directusClient.getCurrentUser();
    return this.mapUserDtoToUser(user);
  }

  async hasActiveJobs(userId: string): Promise<boolean> {
    return directusClient.hasActiveJobs(userId);
  }

  async isContactInJob(userId: string): Promise<boolean> {
    return directusClient.isContactInJob(userId);
  }

  async updateCurrentUser(data: Partial<User>): Promise<UserDto> {
    const directusUser = await directusClient.updateCurrentUser(
      this.mapUserDtoToDirectusUser(data),
    );
    return this.mapDirectusUserToUserDto(directusUser);
  }

  async isLoggedIn(): Promise<boolean> {
    return directusClient.isLoggedIn();
  }

  async refresh(): Promise<boolean> {
    const authData = await directusClient.refresh();
    return !!authData.access_token;
  }

  async deleteCurrentUser(id?: string | null): Promise<void> {
    if (id) {
      await directusClient.deleteCurrentUser(id);
      await this.logout();
    }
  }

  register(
    data: RegistrationDataDTO,
  ): Promise<{ organizationId: string | null }> {
    return directusClient.registerUser(data);
  }

  async requestPasswordReset(
    email?: string | null,
    redirectUrl?: string,
  ): Promise<void> {
    if (email) {
      await directusClient.requestPasswordReset(email, redirectUrl);
    }
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await directusClient.resetPassword(token, password);
  }

  async registerUserVerify(token: string): Promise<void> {
    await directusClient.registerUserVerify(token);
  }

  resendVerification(redirectUrl: string | undefined): Promise<void> {
    return directusClient.resendVerification(redirectUrl);
  }

  private mapDirectusUserToUserDto(
    directusUser: Awaited<ReturnType<DirectusClient['updateCurrentUser']>>,
  ): UserDto {
    const role =
      typeof directusUser.role === 'string'
        ? directusUser.role
        : directusUser.role?.name;

    return {
      id: directusUser.id,
      gender: directusUser.gender,
      role: { name: role ?? '' },
      email: directusUser.email ?? null,
      phone: directusUser.phone ?? null,
      first_name: directusUser.first_name ?? null,
      last_name: directusUser.last_name ?? null,
      avatar: this.transformAvatar(directusUser.avatar),
      applicationNotification: directusUser.applicationNotification,
      jobNoteNotification: directusUser.jobNoteNotification,
      language: directusUser.language,
    };
  }

  private transformAvatar(
    avatar: Awaited<ReturnType<DirectusClient['updateCurrentUser']>>['avatar'],
  ): UserDto['avatar'] {
    if (!avatar || typeof avatar === 'string') {
      return null;
    }
    return {
      id: avatar.id,
      title: avatar.title ?? null,
      filename_disk: avatar.filename_disk ?? null,
    };
  }

  private mapUserDtoToDirectusUser(
    user: Partial<UserDto | User>,
  ): Partial<DirectusUser<CustomDirectusTypes>> {
    return {
      id: user.id,
      gender: user.gender,
      email: user.email,
      phone: user.phone,
      first_name: user.first_name,
      last_name: user.last_name,
      avatar: user.avatar?.id ?? null,
      applicationNotification: user.applicationNotification,
      jobNoteNotification: user.jobNoteNotification,
      language: user.language,
    };
  }
}

const authService = new AuthenticationService();
export default authService;
